import { FormBlock } from '@qbe/ads-blocks-forms';
import { Box, Button, Form, FormGroupLabel, Spinner, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { NumberOfEmployees } from '~/components/NumberOfEmployees';
import BusinessAddressLookup from '~/components-ds/BusinessAddressLookup';
import { SELECT_COVER_ROUTE } from '~/constants/routes';
import { useAppContent, useAppDispatch, useBusinessDetails } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import {
  updateAnnualTurnover,
  updateEmployeeCount,
  updateIsManualEntry,
  updatePropertyValue,
} from '~/slices/businessDetails';
import { qalFormCompleteAction } from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import Turnover from '../Turnover';
import { PropertyValue } from './PropertyValue';
import type { SpecificBusinessDetailsForm } from './types';
const SpecificBusinessDetails: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme',
  });

  const {
    employeeCount,
    annualTurnover,
    addressLine1,
    businessAddress,
    fullBusinessAddress,
    suburb,
    state,
    postcode,
    isValidAddress,
    propertyValue,
    version,
  } = useBusinessDetails();
  const methods = useFormWithAnalytics<SpecificBusinessDetailsForm>({
    mode: 'onChange',
    defaultValues: {
      numberOfEmployees: employeeCount ?? undefined,
      turnover: annualTurnover ?? undefined,
      propertyValue: propertyValue ?? undefined,
    },
  });

  useEffect(() => {
    methods.reset();
  }, [version, methods]);

  const [isFetchingAddress, setIsFetchingAddress] = React.useState(false);
  // Use global state for isManualEntry
  const { isManualEntry } = useBusinessDetails();

  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const dispatch = useAppDispatch();

  const navigate = useAppNavigate();

  const isSpecificBusinessDetailsValid =
    methods.formState.isValid &&
    (isManualEntry
      ? suburb && state && postcode && isValidAddress
      : businessAddress && fullBusinessAddress);

  // Sitecore content
  const addressExampleDescription = getJSXContent(
    'addressComponent.contextualhelpers.addressExample.description'
  );
  const addressInputTitle = isPropertyOwner
    ? getTextContent('businessDetailsPropertyDetails.fields.addressInput.title')
    : getTextContent('businessDetailsSpecificBusinessDetails.fields.addressInput.title');

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  useEffect(() => {
    if (addressLine1) {
      dispatch(updateIsManualEntry(true));
    }
  }, [addressLine1, dispatch]);

  const onSubmit = methods.handleSubmit((data?: SpecificBusinessDetailsForm) => {
    if (!data) return;
    dispatch(updateAnnualTurnover(data.turnover ?? undefined));
    if (isPropertyOwner) {
      dispatch(updatePropertyValue(data.propertyValue ?? undefined));
    } else {
      dispatch(updateEmployeeCount(data.numberOfEmployees ?? undefined));
    }
    handleFormComplete();
    dispatch(
      navigateAction({
        to: SELECT_COVER_ROUTE,
        navigate,
      })
    );
  });

  return (
    <FormProvider {...methods}>
      <Form noValidate autoComplete="off" onSubmit={onSubmit}>
        {isManualEntry ? (
          <FormGroupLabel
            alignCenter
            title={addressInputTitle}
            subtitle={
              isPropertyOwner
                ? ''
                : getTextContent(
                    'businessDetailsSpecificBusinessDetails.fields.addressInput.description'
                  )
            }
            titleSuffix={
              <ContextualHelpWithButton>
                <Typography>{addressExampleDescription}</Typography>
              </ContextualHelpWithButton>
            }
          />
        ) : (
          <FormGroupLabel
            alignCenter
            title={addressInputTitle}
            subtitle={
              isPropertyOwner
                ? ''
                : getTextContent(
                    'businessDetailsSpecificBusinessDetails.fields.addressInput.description'
                  )
            }
          />
        )}

        <BusinessAddressLookup onLoadingChange={setIsFetchingAddress} />

        {!isPropertyOwner && <NumberOfEmployees />}
        <Turnover />
        {isPropertyOwner && (
          <Box textAlign="center">
            <PropertyValue />
          </Box>
        )}

        <Spinner loading={isFetchingAddress} description={getSharedContent('loadingLabel')} />
        <FormBlock preset="button">
          <Button
            data-track="Next"
            type="submit"
            disabled={!isSpecificBusinessDetailsValid || isFetchingAddress}
            label={getSharedContent('nextStepButtonLabel')}
          />
        </FormBlock>
      </Form>
    </FormProvider>
  );
};

export default SpecificBusinessDetails;
